import { SITE_URL } from '@/config/site';
import { noteCategories, getArticleBySlug } from '@/mocks/notes';
import type { HeadSeo } from './head';

export interface ResolvedSeo {
  head: HeadSeo;
  schema?: object;
}

const DEFAULT_KEYWORDS = '古箏教學,台中古箏,豐原古箏,選箏諮詢,成人古箏';

interface HeadInput {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
  ogUrl?: string;
  ogType?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  twitterImage?: string;
  robots?: string;
}

function buildHead(input: HeadInput): HeadSeo {
  return {
    title: input.title,
    description: input.description,
    keywords: input.keywords ?? DEFAULT_KEYWORDS,
    robots: input.robots ?? 'index, follow',
    canonical: input.canonical,
    ogTitle: input.title,
    ogDescription: input.description,
    ogUrl: input.ogUrl ?? input.canonical,
    ogType: input.ogType ?? 'website',
    ogImage: input.ogImage,
    articlePublishedTime: input.articlePublishedTime,
    twitterCard: 'summary_large_image',
    twitterTitle: input.title,
    twitterDescription: input.description,
    twitterImage: input.twitterImage,
  };
}

// ---------- Home ----------
const homeSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: '鳴箏',
      description: '台中豐原古箏教學品牌，提供一對一陪伴式古箏教學與選箏諮詢服務。',
      inLanguage: 'zh-Hant-TW',
      potentialAction: {
        '@type': 'SearchAction',
        target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/notes?search={search_term_string}` },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'MusicSchool',
      '@id': `${SITE_URL}/#organization`,
      name: '鳴箏',
      url: SITE_URL,
      description: '台中豐原古箏教學品牌，提供一對一陪伴式古箏教學、選箏諮詢服務。適合成人初學者與孩童，以慢、穩、安心的節奏讓音樂回到生活。',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '豐原大道一段269-1號',
        addressLocality: '豐原區',
        addressRegion: '台中市',
        addressCountry: 'TW',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 24.2524,
        longitude: 120.7217,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '10:00',
          closes: '21:00',
        },
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
      sameAs: [
        'https://www.instagram.com/',
        'https://www.facebook.com/',
      ],
    },
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/#webpage`,
      url: SITE_URL,
      name: '鳴箏｜台中豐原古箏教學・陪伴式學習・選箏諮詢',
      description: '鳴箏古箏教學品牌，位於台中豐原，提供一對一陪伴式古箏教學、選箏諮詢服務。',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'zh-Hant-TW',
    },
  ],
};

// ---------- About ----------
const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      '@id': `${SITE_URL}/about#webpage`,
      url: `${SITE_URL}/about`,
      name: '關於鳴箏｜古箏老師介紹・教學理念・學習歷程',
      description: '認識鳴箏古箏老師的學習歷程、教學資歷與品牌理念。20年以上古箏演奏經驗，累積逾200位學員，台中豐原實體教室。',
      inLanguage: 'zh-Hant-TW',
      isPartOf: { '@id': `${SITE_URL}/#website` },
    },
    {
      '@type': 'Person',
      '@id': `${SITE_URL}/about#teacher`,
      name: '鳴箏老師',
      jobTitle: '古箏教師',
      description: '古箏演奏資歷20年以上，累積教學學員逾200位，正式教學年資8年以上，台中豐原實體教室。',
      worksFor: { '@id': `${SITE_URL}/#organization` },
      knowsAbout: ['古箏教學', '古箏演奏', '音樂教育', '選箏諮詢'],
      address: {
        '@type': 'PostalAddress',
        addressLocality: '豐原區',
        addressRegion: '台中市',
        addressCountry: 'TW',
      },
    },
  ],
};

// ---------- Teaching ----------
const teachingSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/teaching#webpage`,
      url: `${SITE_URL}/teaching`,
      name: '教學對象與方式｜鳴箏古箏教學・台中豐原一對一課程',
      description: '了解鳴箏古箏教學對象、上課方式與學習節奏說明。提供一對一個人課與小班制共學課，適合成人初學者與孩童，台中豐原。',
      inLanguage: 'zh-Hant-TW',
      isPartOf: { '@id': `${SITE_URL}/#website` },
    },
    {
      '@type': 'Course',
      name: '一對一古箏個人課',
      description: '完全依照你的程度、狀態與節奏設計課程內容，每堂課量身訂製，適合成人初學者與孩童。',
      provider: { '@id': `${SITE_URL}/#organization` },
      courseMode: ['onsite'],
      inLanguage: 'zh-Hant-TW',
      offers: {
        '@type': 'Offer',
        category: '古箏教學',
        availableAtOrFrom: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '豐原大道一段269-1號',
            addressLocality: '豐原區',
            addressRegion: '台中市',
            addressCountry: 'TW',
          },
        },
      },
    },
  ],
};

// ---------- Courses ----------
const coursesSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/courses#webpage`,
      url: `${SITE_URL}/courses`,
      name: '課程與學習路徑｜鳴箏古箏教學・台中豐原',
      description: '鳴箏古箏課程說明，包含初學階段、穩定練習、深化調整三個學習路徑，以及學員心聲與費用說明。台中豐原古箏教學。',
      inLanguage: 'zh-Hant-TW',
      isPartOf: { '@id': `${SITE_URL}/#website` },
    },
    {
      '@type': 'ItemList',
      name: '鳴箏古箏學習階段',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '認識聲音，建立安全感',
          description: '從接觸古箏的聲音開始，不急著彈曲子，讓手指習慣琴弦、讓耳朵熟悉音色，讓學習本身成為一件安心的事。',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '讓音樂走進日常',
          description: '開始有了自己喜歡的曲目，練習逐漸成為日常習慣的一部分，重視規律與感受。',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: '走出自己的聲音',
          description: '技巧漸穩之後，開始探索自己的表達方式，讓音樂成為你真正的語言。',
        },
      ],
    },
  ],
};

// ---------- Instrument ----------
const instrumentSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/instrument#webpage`,
      url: `${SITE_URL}/instrument`,
      name: '古箏買賣・選箏諮詢｜鳴箏・台中豐原古箏',
      description: '鳴箏提供古箏買賣與選箏諮詢服務，涵蓋入門練習箏、中階演奏箏、高階演奏箏，費用NT$12,000起，依學習階段推薦，不強迫推銷。台中豐原。',
      inLanguage: 'zh-Hant-TW',
      isPartOf: { '@id': `${SITE_URL}/#website` },
    },
    {
      '@type': 'Service',
      name: '古箏選購諮詢服務',
      description: '以學習者角度陪伴你選箏，提供入門練習箏至高階演奏箏的建議，先諮詢需求再推薦，不急著購買、不強迫推銷。',
      provider: { '@id': `${SITE_URL}/#organization` },
      areaServed: {
        '@type': 'City',
        name: '台中市',
      },
      offers: [
        {
          '@type': 'Offer',
          name: '入門練習箏',
          description: '適合初學者的古箏，音準穩定、弦距舒適',
          priceCurrency: 'TWD',
          price: '12000',
          priceSpecification: {
            '@type': 'PriceSpecification',
            minPrice: 12000,
            maxPrice: 20000,
            priceCurrency: 'TWD',
          },
        },
        {
          '@type': 'Offer',
          name: '中階演奏箏',
          description: '適合穩定練習者，音色細膩豐富',
          priceCurrency: 'TWD',
          priceSpecification: {
            '@type': 'PriceSpecification',
            minPrice: 20000,
            maxPrice: 45000,
            priceCurrency: 'TWD',
          },
        },
        {
          '@type': 'Offer',
          name: '高階演奏箏',
          description: '適合進階演奏者，頂級工藝與音色',
          priceCurrency: 'TWD',
          priceSpecification: {
            '@type': 'PriceSpecification',
            minPrice: 50000,
            priceCurrency: 'TWD',
          },
        },
      ],
    },
  ],
};

// ---------- Contact ----------
const contactSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ContactPage',
      '@id': `${SITE_URL}/contact#webpage`,
      url: `${SITE_URL}/contact`,
      name: '聯絡鳴箏｜半價體驗課預約・台中豐原古箏教學諮詢',
      description: '預約鳴箏半價體驗課，或透過 LINE、Instagram、Facebook 聯絡我們。台中豐原豐原大道一段269-1號。',
      inLanguage: 'zh-Hant-TW',
      isPartOf: { '@id': `${SITE_URL}/#website` },
    },
    {
      '@type': 'MusicSchool',
      '@id': `${SITE_URL}/#organization`,
      name: '鳴箏',
      url: SITE_URL,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '豐原大道一段269-1號',
        addressLocality: '豐原區',
        addressRegion: '台中市',
        addressCountry: 'TW',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 24.2524,
        longitude: 120.7217,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '10:00',
          closes: '21:00',
        },
      ],
    },
  ],
};

// ---------- Notes ----------
const NOTES_BASE_TITLE = '鳴箏筆記｜古箏學習文章・選箏指南・初學入門';
const NOTES_DEFAULT_DESC = '鳴箏老師分享的古箏學習筆記，涵蓋初學入門、學習陪伴、選箏指南、常見問題與老師心得。台中豐原古箏教學。';

const notesSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/notes#webpage`,
      url: `${SITE_URL}/notes`,
      name: NOTES_BASE_TITLE,
      description: NOTES_DEFAULT_DESC,
      inLanguage: 'zh-Hant-TW',
      isPartOf: { '@id': `${SITE_URL}/#website` },
    },
    {
      '@type': 'Blog',
      '@id': `${SITE_URL}/notes#blog`,
      url: `${SITE_URL}/notes`,
      name: '鳴箏筆記',
      description: '古箏老師的學習筆記與分享，涵蓋選箏、入門、練習心法等主題。',
      publisher: { '@id': `${SITE_URL}/#organization` },
      inLanguage: 'zh-Hant-TW',
    },
  ],
};

function notesSeo(search: string): ResolvedSeo {
  const categoryParam = new URLSearchParams(search).get('category');
  const cat = noteCategories.find((c) => c.id === categoryParam);

  if (cat?.seoDescription) {
    return {
      head: buildHead({
        title: `${cat.label}｜鳴箏筆記`,
        description: cat.seoDescription,
        keywords: '古箏文章,古箏筆記,選箏指南,古箏初學',
        canonical: `${SITE_URL}/notes`,
        ogUrl: `${SITE_URL}/notes?category=${cat.id}`,
      }),
      schema: notesSchema,
    };
  }

  return {
    head: buildHead({
      title: NOTES_BASE_TITLE,
      description: NOTES_DEFAULT_DESC,
      keywords: '古箏文章,古箏筆記,選箏指南,古箏初學',
      canonical: `${SITE_URL}/notes`,
    }),
    schema: notesSchema,
  };
}

// ---------- Article ----------
function articleSeo(pathname: string): ResolvedSeo {
  const slug = pathname.replace('/notes/', '');
  const article = getArticleBySlug(slug);

  if (!article) {
    return notesSeo('');
  }

  const desc = article.excerpt || `${article.title} — 鳴箏古箏老師筆記，台中豐原古箏教學。`;
  const articleUrl = `${SITE_URL}/notes/${article.slug}`;
  const keywords = article.seoKeywords?.length
    ? `古箏,${article.seoKeywords.join(',')}`
    : '古箏,古箏文章,古箏筆記';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: desc,
    url: articleUrl,
    image: article.coverImage,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: '鳴箏老師',
      url: `${SITE_URL}/about`,
    },
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    inLanguage: 'zh-Hant-TW',
    keywords: `古箏,${article.categoryLabel || ''},${article.title}`,
  };

  return {
    head: buildHead({
      title: `${article.title}｜鳴箏筆記`,
      description: desc,
      keywords,
      canonical: articleUrl,
      ogType: 'article',
      ogImage: article.coverImage,
      articlePublishedTime: article.publishedAt,
      twitterImage: article.coverImage,
    }),
    schema,
  };
}

// ---------- NotFound ----------
function notFoundSeo(): ResolvedSeo {
  return {
    head: buildHead({
      title: '頁面不存在｜鳴箏',
      description: '找不到您要的頁面。歡迎回到鳴箏官網，探索台中豐原古箏教學、選箏諮詢與學習筆記。',
      keywords: DEFAULT_KEYWORDS,
      canonical: SITE_URL,
      robots: 'noindex, follow',
    }),
  };
}

export function resolveSeo(pathname: string, search: string): ResolvedSeo {
  if (pathname === '/') {
    return {
      head: buildHead({
        title: '鳴箏｜台中豐原古箏教學・陪伴式學習・選箏諮詢',
        description: '鳴箏古箏教學品牌，位於台中豐原，提供一對一陪伴式古箏教學、選箏諮詢服務。適合成人初學者與孩童，以慢、穩、安心的節奏讓音樂回到生活。',
        canonical: SITE_URL,
        ogImage: 'https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/3c50ed1f5eceb24b07ee4f0a201007d7.png',
        twitterImage: 'https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/3c50ed1f5eceb24b07ee4f0a201007d7.png',
      }),
      schema: homeSchema,
    };
  }

  if (pathname === '/about') {
    return {
      head: buildHead({
        title: '關於鳴箏｜古箏老師介紹・教學理念・學習歷程',
        description: '認識鳴箏古箏老師的學習歷程、教學資歷與品牌理念。20年以上古箏演奏經驗，累積逾200位學員，台中豐原實體教室。',
        keywords: '鳴箏老師,古箏老師,教學理念,台中古箏',
        canonical: `${SITE_URL}/about`,
      }),
      schema: aboutSchema,
    };
  }

  if (pathname === '/teaching') {
    return {
      head: buildHead({
        title: '教學對象與方式｜鳴箏古箏教學・台中豐原一對一課程',
        description: '了解鳴箏古箏教學對象、上課方式與學習節奏說明。提供一對一個人課與小班制共學課，適合成人初學者與孩童，台中豐原。',
        keywords: '古箏教學,一對一古箏,成人古箏,台中豐原',
        canonical: `${SITE_URL}/teaching`,
      }),
      schema: teachingSchema,
    };
  }

  if (pathname === '/courses') {
    return {
      head: buildHead({
        title: '課程與學習路徑｜鳴箏古箏教學・台中豐原',
        description: '鳴箏古箏課程說明，包含初學階段、穩定練習、深化調整三個學習路徑，以及學員心聲與費用說明。台中豐原古箏教學。',
        keywords: '古箏課程,學習路徑,古箏教學,台中豐原',
        canonical: `${SITE_URL}/courses`,
      }),
      schema: coursesSchema,
    };
  }

  if (pathname === '/instrument') {
    return {
      head: buildHead({
        title: '古箏買賣・選箏諮詢｜鳴箏・台中豐原古箏',
        description: '鳴箏提供古箏買賣與選箏諮詢服務，涵蓋入門練習箏、中階演奏箏、高階演奏箏，費用NT$12,000起，依學習階段推薦，不強迫推銷。台中豐原。',
        keywords: '古箏買賣,選箏諮詢,古箏價格,台中古箏',
        canonical: `${SITE_URL}/instrument`,
      }),
      schema: instrumentSchema,
    };
  }

  if (pathname === '/contact') {
    return {
      head: buildHead({
        title: '聯絡鳴箏｜半價體驗課預約・台中豐原古箏教學諮詢',
        description: '預約鳴箏半價體驗課，或透過 LINE、Instagram、Facebook 聯絡我們。台中豐原豐原大道一段269-1號。',
        keywords: '古箏體驗課,課程預約,台中古箏,豐原古箏',
        canonical: `${SITE_URL}/contact`,
      }),
      schema: contactSchema,
    };
  }

  if (pathname === '/notes') {
    return notesSeo(search);
  }

  if (pathname.startsWith('/notes/')) {
    return articleSeo(pathname);
  }

  return notFoundSeo();
}